import { createResource, createMemo, Accessor } from "solid-js";
import { fetchCampaignTiles } from "@/utils/checkout.utils";
import { calculateAvailableSelections } from "@/utils/parsedata.utils";
import { useSelectionAlerts } from "@/hooks/useSelectionAlerts";
import { CheckoutAlertType } from "@/components/checkout-alert/CheckoutAlert";
import { CancelCampaignParams } from "@/Main";
import { FeatureToggle } from "@/shared/enums/feature-toggle.enums";

interface UseCampaignDataProps {
  shouldFetchData?: Accessor<boolean>;
  dealerId: Accessor<string | undefined>;
  featureToggles: Accessor<Record<string, boolean> | undefined>;
  cancelCampaign: Accessor<CancelCampaignParams | undefined>;
}

const MAX_BOOKING_WINDOW = 12;
const MAX_SELECTIONS = 3;

export const useCampaignData = (props: UseCampaignDataProps) => {
  const shouldFetchCampaignData = createMemo(
    () => (props.shouldFetchData?.() ?? false) && !props.cancelCampaign(),
  );

  const fetchMonthsLimit = createMemo(() => {
    const toggles = props.featureToggles();
    if (!toggles) return MAX_BOOKING_WINDOW;

    if (toggles[FeatureToggle.FEATURED_DEALER_3_MONTHS] === true) {
      return 3; // 3-month window takes precedence when both toggles enabled
    }

    if (toggles[FeatureToggle.FEATURED_DEALER_6_MONTHS] === true) {
      return 6;
    }

    return MAX_BOOKING_WINDOW;
  });

  const [campaignData] = createResource(
    () => {
      const id = props.dealerId();
      return shouldFetchCampaignData() && id
        ? { dealerId: id, limit: fetchMonthsLimit() }
        : null;
    },
    (params) => fetchCampaignTiles(params.dealerId, params.limit),
  );

  const tiles = createMemo(() => campaignData()?.tiles ?? []);

  const adjustedMaxSelections = createMemo(() =>
    calculateAvailableSelections(tiles(), MAX_SELECTIONS),
  );

  const { hasReachedMaxBookings, hasNoAvailableSlots } = useSelectionAlerts(
    tiles,
    adjustedMaxSelections,
  );

  const alertType = createMemo<CheckoutAlertType | undefined>(() => {
    if (!(props.shouldFetchData?.() ?? false) || campaignData.loading)
      return undefined;
    if (hasReachedMaxBookings()) return "maxscheduled";
    if (hasNoAvailableSlots()) return "soldout";
    return undefined;
  });

  const isLoadingCampaignData = createMemo(() => {
    if (!(props.shouldFetchData?.() ?? false)) return false;
    return campaignData.loading;
  });

  const hasErrors = createMemo(() => !!campaignData.error);

  return {
    tiles,
    adjustedMaxSelections,
    alertType,
    isLoading: isLoadingCampaignData,
    hasErrors,
  };
};
